import React, { useEffect, useState } from 'react';
import CollectionItem from 'api/models/CollectionItem';
import { createUseStyles } from 'react-jss';
import styles from './Swimlane.styles';
import TileComponent from '../TileComponent';
import classNames from 'classnames';
import SwimlaneArrow from './SwimlaneArrow';
import { isEmpty, isSafari } from 'util/index';
import useBrowser from '../hooks/useBrowser';
import { breakpoints } from 'components/theme/breakpoints';
import logger from 'util/logger';

type SwimlaneProps = {
  display: string;
  contentTypeId: string;
  items: Array<unknown>;
  tileComponent: string;
  showControlButton?: boolean;
  collectionId: string;
  title?: string;
  rows?: number;
} & Record<string, unknown>;

interface ScrollEvent {
  target: HTMLDivElement;
}

const useStyles = createUseStyles(styles, { name: 'Swimlane' });
const Swimlane = ({ items, tileComponent, rows = 1, ...rest }: SwimlaneProps): JSX.Element => {
  const classes = useStyles();

  const [directions, setDirections] = useState({
    left: false,
    right: false,
  });

  const browser = useBrowser();
  const swimlaneId = (rest.collectionId || rest.slug) as string;

  const handleScroll = (e: ScrollEvent | Event) => {
    const wrapper = e.target as HTMLDivElement;
    const swimlane = document.getElementById(swimlaneId);
    if (!swimlaneId) {
      logger.error('swimlaneId is undefined in Swimlane.ts');
      return;
    }

    const leftOffset = browser.down(breakpoints.sm) ? 20 : 40;
    const rightOffset = browser.down(breakpoints.sm) ? 15 : 45;

    let nextDirections = {};
    if (wrapper.scrollLeft - leftOffset > swimlane?.scrollLeft && !directions.left) {
      nextDirections = {
        ...nextDirections,
        left: true,
      };
    }
    if (wrapper.scrollLeft - leftOffset <= swimlane?.scrollLeft && directions.left) {
      nextDirections = {
        ...nextDirections,
        left: false,
      };
    }

    const rightCorner = wrapper.scrollWidth - wrapper.scrollLeft - rightOffset - leftOffset;

    if (rightCorner > swimlane?.clientWidth && !directions.right) {
      nextDirections = {
        ...nextDirections,
        right: true,
      };
    }

    if (rightCorner <= swimlane?.clientWidth && directions.right) {
      nextDirections = {
        ...nextDirections,
        right: false,
      };
    }
    if (!isEmpty(nextDirections)) {
      setDirections({
        ...directions,
        ...nextDirections,
      });
    }
  };

  useEffect(() => {
    if (swimlaneId) {
      handleScroll({
        target: document.getElementById(`wrapper-${swimlaneId}`) as HTMLDivElement,
      });
    }
  }, [browser.size, swimlaneId]);

  useEffect(() => {
    const scrollElem = document.getElementById(`wrapper-${swimlaneId}`);
    scrollElem.addEventListener('scroll', handleScroll);

    return () => {
      scrollElem.removeEventListener('scroll', handleScroll);
    };
  }, [directions]);

  // TODO: tile component prop hasActionButtons={match.path !== '/radiozenders'}
  const renderTile = (tile: CollectionItem) => (
    <div key={tile.id || tile.slug} className={classNames(classes.item, classes[tileComponent])}>
      <TileComponent tileComponent={tileComponent} {...rest} {...tile} />
    </div>
  );

  const scroll = (direction = 'right') => () => {
    const wrapper = document.getElementById(`wrapper-${swimlaneId}`);
    // 50% for small screens
    let percentage = 0.5;

    if (browser.up(breakpoints.ml)) {
      // 25% for other screens
      percentage = 0.25;
    }

    const amount = window.innerWidth * percentage;

    const left = direction === 'right' ? wrapper.scrollLeft + amount : wrapper.scrollLeft - amount;

    if (isSafari) {
      const smoothScroll = (nextPoint: number) => {
        wrapper.scrollTo({
          left: nextPoint,
          behavior: 'smooth',
        });
        if (direction === 'right' && nextPoint >= left) {
          return;
        }
        if (direction === 'left' && nextPoint <= left) {
          return;
        }
        setTimeout(() => smoothScroll(direction === 'right' ? nextPoint + 10 : nextPoint - 10));
      };

      smoothScroll(wrapper.scrollLeft);
      return;
    }

    wrapper.scrollTo({
      left,
      behavior: 'smooth',
    });
  };

  const { left = false, right = false } = directions;

  const itemsPerRow = {};
  let rowNum = 0;
  const rowList = [...Array(rows)].map((a, idx) => idx);

  items.forEach((item) => {
    rowList.some((row) => {
      if (rowNum === row) {
        if (!itemsPerRow[row]) {
          itemsPerRow[row] = [];
        }
        itemsPerRow[row].push(item);
        return true;
      }
    });
    rowNum += 1;
    if (rowNum >= rows) {
      rowNum = 0;
    }
  });

  return (
    <div data-testid={`swimlane-${rest.contentTypeId}-${rest.slug}`} id={swimlaneId} className={classes.swimlane}>
      <SwimlaneArrow visible={left} handleClick={scroll('left')} direction="left" />
      <div id={`wrapper-${swimlaneId}`} key={swimlaneId} className={classes.wrapper}>
        <div className={classes.section}>
          {Object.values(itemsPerRow).map((section: Array<unknown>, idx) => (
            <div key={idx} className={classes.items}>
              {section.map(renderTile)}
            </div>
          ))}
        </div>
      </div>
      <SwimlaneArrow visible={right} handleClick={scroll()} direction="right" />
    </div>
  );
};

export default Swimlane;
